import type { ApiClient } from '../api/client';
import type { Mirror } from './mirror';
import type { Outbox } from '../outbox/outbox';
import { StreamEventData } from '../wire';
import { readSse } from './sse';

/**
 * The connection: hydrate, stream, drain, and do it again when the tunnel ends.
 *
 * This is the only object in the app that knows whether there is a network. It
 * owns the loop and publishes a `ConnectionState`; everything else reads the
 * mirror and appends to the outbox regardless.
 *
 * ## The loop
 *
 * 1. **Hydrate** — refill the working set (spec §8.1, "refreshed on connect").
 * 2. **Drain** — replay whatever the device owes, in order (§8.2, "on reconnect
 *    it replays in order"). After hydrate, so that a rejected intent's task is
 *    built against a mirror that already knows who took the slot.
 * 3. **Stream** — `GET /v1/events/stream`, resumed from the stored cursor, each
 *    event a targeted refetch (§8.1, "and by SSE while online").
 * 4. When the stream ends for any reason — a tunnel, a proxy timeout, an iPad
 *    going to sleep — back off and start again at 1.
 *
 * ## Why hydrate on every reconnect rather than trusting the cursor
 *
 * The cursor makes the *stream* resumable and `routes/events.ts` is explicit
 * that this is what it is for. It does not make the mirror correct: `events` is
 * retained for 24 months but the stream replays at most `SSE_REPLAY_LIMIT` per
 * poll, a device can be off for a week, and — the case that actually decides it
 * — a hydrate is the only thing that can notice an appointment that is *gone*.
 * A full refill of a bounded window costs five small requests. Being subtly
 * wrong about the book costs a double booking.
 */

export type ConnectionState = 'offline' | 'connecting' | 'live';

export interface SyncOptions {
  api: ApiClient;
  mirror: Mirror;
  outbox: Outbox;
  onState: (state: ConnectionState) => void;
  now?: () => number;
  /** Injected in tests; production waits for real. */
  sleep?: (ms: number) => Promise<void>;
  /**
   * Abort a stream that has gone silent for this long. The server heartbeats
   * every `SSE_KEEPALIVE_MS` (25 s), so silence past ~2× that means the
   * upstream is gone while something in the middle — a proxy, a load balancer,
   * the Vite dev server — is holding our connection open. Found live: killing
   * `core-api` behind the dev proxy left the app parked in a silent stream,
   * believing it was online, with a queued booking it would never replay.
   */
  stallMs?: number;
  /**
   * While the stream is live, re-run the outbox drain this often. The drain at
   * the top of the cycle stops at an entry still inside its backoff (order
   * holds), and a healthy stream can then block the loop for hours — without
   * this tick, that entry's retry would wait for the next disconnect.
   */
  drainIntervalMs?: number;
  /**
   * Every frame, after the mirror has had it — the **cue tap**.
   *
   * The mirror's `apply` is the offline half of this stream: it turns a booking
   * event into a targeted refetch of a row it keeps. The online screens (spec
   * §3.6's kitchen, §4.8's dock) keep no rows, so there is nothing for `apply`
   * to do for them and nothing they want written to IndexedDB. What they want
   * is the *cue*: `order.ready` arrived, re-read the list. Handing it to them
   * here costs one callback and keeps the boundary intact — nothing about this
   * hook can put an order in the mirror, because the hook cannot write.
   *
   * It fires after `apply`, so a listener that reads the mirror sees the same
   * event's effect rather than the state before it.
   */
  onEvent?: (type: string, subjectId: string | null) => void;
}

const MAX_BACKOFF_MS = 30_000;
const DEFAULT_STALL_MS = 60_000;
const DEFAULT_DRAIN_INTERVAL_MS = 5_000;

export class Sync {
  private readonly api: ApiClient;
  private readonly mirror: Mirror;
  private readonly outbox: Outbox;
  private readonly onState: (state: ConnectionState) => void;
  private readonly sleep: (ms: number) => Promise<void>;
  private controller: AbortController | null = null;
  private running = false;
  private failures = 0;
  private state: ConnectionState = 'offline';
  private readonly stallMs: number;
  private readonly drainIntervalMs: number;
  private readonly onEvent: (type: string, subjectId: string | null) => void;

  constructor(options: SyncOptions) {
    this.onEvent = options.onEvent ?? ((): void => undefined);
    this.api = options.api;
    this.mirror = options.mirror;
    this.outbox = options.outbox;
    this.onState = options.onState;
    this.sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    this.stallMs = options.stallMs ?? DEFAULT_STALL_MS;
    this.drainIntervalMs = options.drainIntervalMs ?? DEFAULT_DRAIN_INTERVAL_MS;
  }

  current(): ConnectionState {
    return this.state;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    void this.loop();
  }

  stop(): void {
    this.running = false;
    this.controller?.abort();
    this.controller = null;
    this.setState('offline');
  }

  /** The browser told us the radio came back. Cut the backoff short. */
  wake(): void {
    if (!this.running) return;
    this.failures = 0;
    this.controller?.abort();
  }

  /** One pass. Exposed for tests, which drive the cycle rather than the clock. */
  async cycle(): Promise<void> {
    const controller = new AbortController();
    this.controller = controller;
    this.setState('connecting');

    await this.mirror.hydrate();
    await this.outbox.drain();
    await this.outbox.prune();
    this.failures = 0;
    this.setState('live');

    const cursor = await this.mirror.cursor();
    const response = await this.api.stream(cursor, controller.signal);

    // The stall watchdog. Found live, not imagined: killing `core-api` behind
    // the Vite proxy left this stream OPEN and SILENT — the proxy held our
    // connection while the upstream was gone — so the app sat "live" for ten
    // minutes with a queued booking it never replayed. The server heartbeats
    // every 25 s for exactly this moment; if nothing arrives (frames or
    // keepalive comments — `readSse`'s onActivity sees both) inside `stallMs`,
    // the stream is dead whatever the socket says, and aborting it sends the
    // loop back to a hydrate that will fail honestly or reconnect cleanly.
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const armStall = (): void => {
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => controller.abort(), this.stallMs);
    };

    // The drain tick. The drain above stops at an entry still inside its
    // backoff (order holds), and a healthy stream can block this cycle for
    // hours — without the tick, that entry's retry would wait for the next
    // disconnect. Cheap: a drain over an empty queue reads one index.
    const drainTick = (async (): Promise<void> => {
      for (;;) {
        await this.sleep(this.drainIntervalMs);
        if (controller.signal.aborted) return;
        try {
          await this.outbox.drain();
        } catch {
          // The next cycle's hydrate will notice whatever this was.
        }
      }
    })();
    void drainTick;

    try {
      armStall();
      for await (const frame of readSse(response, armStall)) {
        // The abort signal, not the `running` flag: `stop()` aborts the
        // controller, and a cycle driven directly (by a test, or by a future
        // "sync now" button) has no loop to have set a flag.
        if (controller.signal.aborted) break;

        if (frame.event === 'stream.error') break;
        if (frame.event !== 'stream.open') {
          const parsed = StreamEventData.safeParse(safeJson(frame.data));
          if (parsed.success) {
            await this.mirror.apply(parsed.data.type, parsed.data.subject_id);
            this.onEvent(parsed.data.type, parsed.data.subject_id);
          }
        }
        // The cursor advances only after the frame has been handled — a device
        // that dies here re-reads the event rather than skipping it, and every
        // handler above is a refetch, which is idempotent by construction.
        if (frame.id !== null && frame.id !== '') await this.mirror.setCursor(frame.id);
      }
    } finally {
      if (stallTimer !== null) clearTimeout(stallTimer);
      // End the drain tick with the cycle; its pending sleep resolves once
      // more, sees the abort, and returns.
      controller.abort();
    }
  }

  private async loop(): Promise<void> {
    while (this.running) {
      try {
        await this.cycle();
      } catch {
        // Every failure in the cycle is the same event to this loop: we are not
        // connected. Which of the five requests failed is a log line, not a
        // decision — the recovery is identical.
        this.failures += 1;
        this.setState('offline');
      }
      if (!this.running) return;
      await this.sleep(this.backoff());
    }
  }

  private backoff(): number {
    if (this.failures === 0) return 250; // A clean stream end: reconnect promptly.
    return Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.failures - 1, 5));
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.onState(state);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
